import { createContext, useContext, useMemo, useState } from 'react';
import type { TimelineRange } from '../atoms/Timeline';

export interface CarePlanTimelineScope {
  /**
   * The active topic's time window. When a topic detail overlay is open, the structural timeline
   * rescales to this range so it aligns with the topic's stacked charts. Null when no overlay is
   * open — the timeline falls back to its full derived range (all encounters).
   */
  topicRange: TimelineRange | null;
  setTopicRange: (range: TimelineRange | null) => void;
}

const defaultValue: CarePlanTimelineScope = {
  topicRange: null,
  setTopicRange: () => undefined,
};

export const CarePlanTimelineScopeContext = createContext<CarePlanTimelineScope>(defaultValue);

export function useCarePlanTimelineScope(): CarePlanTimelineScope {
  return useContext(CarePlanTimelineScopeContext);
}

/**
 * Owns the timeline-scope state and builds the {@link CarePlanTimelineScope} value to provide via
 * {@link CarePlanTimelineScopeContext}. A topic detail overlay sets the range; null restores the
 * timeline's full derived range.
 */
export function useCarePlanTimelineScopeState(): CarePlanTimelineScope {
  const [topicRange, setTopicRange] = useState<TimelineRange | null>(null);
  return useMemo<CarePlanTimelineScope>(() => ({ topicRange, setTopicRange }), [topicRange]);
}
